import cornerstoneTools from 'cornerstone-tools';
import _ from "lodash";

const numbersWithCommas = cornerstoneTools.importInternal('util/numbersWithCommas');
const getPixelSpacing = cornerstoneTools.importInternal('util/getPixelSpacing');
// Drawing
const getNewContext = cornerstoneTools.import('drawing/getNewContext');
const draw = cornerstoneTools.import('drawing/draw');
const drawJoinedLines = cornerstoneTools.import('drawing/drawJoinedLines');
const drawLinkedTextBox = cornerstoneTools.importInternal('drawing/drawLinkedTextBox');
const drawEllipse = cornerstoneTools.import('drawing/drawEllipse');
const drawHandles = cornerstoneTools.import('drawing/drawHandles')
const freehandRoiCursor = cornerstoneTools.importInternal('cursors/index');

const freehand = cornerstoneTools.importInternal('util/freehandUtils');

const FreehandRoiTool = cornerstoneTools.FreehandRoiTool;//cornerstoneTools.importInternal('annotation/FreehandRoiTool');

/**
 * @public
 * @class FreehandRoiTool
 * @memberof Tools.Annotation
 * @classdesc Tool for drawing arbitrary polygonal regions of interest, and
 * measuring the statistics of the enclosed pixels.
 * @extends Tools.Base.BaseAnnotationTool
 */
export default class FreehandRoiPDTToolEyonix extends FreehandRoiTool {
    constructor(props = {}) {
        const defaultProps = {
            name: 'FreehandPDT',
            supportedInteractionTypes: ['Mouse', 'Touch'],
            configuration: defaultFreehandConfiguration(),
            svgCursor: freehandRoiCursor,
        };

        super(_.extend({name: "FreehandPDT"}, props), defaultProps);

        //console.log("this.name", this.name);
    }

    createNewMeasurement(eventData) {
        const goodEventData =
            eventData && eventData.currentPoints && eventData.currentPoints.image;

        if (!goodEventData) {
            // logger.error(
            //     `required eventData not supplied to tool ${this.name}'s createNewMeasurement`
            // );

            return;
        }

        const measurementData = {
            visible: true,
            active: true,
            invalidated: true,
            color: undefined,
            fontSize: undefined,
            who: undefined,
            when: undefined,
            whenStr: undefined,
            textVisible: undefined,
            handles: {
                points: [],
            },
        };

        measurementData.handles.textBox = {
            active: false,
            hasMoved: false,
            movesIndependently: false,
            drawnIndependently: true,
            allowedOutsideImage: true,
            hasBoundingBox: true,
        };

        return measurementData;
    }

    renderToolData(evt) {

        const eventData = evt.detail;

        // If we have no toolState for this element, return immediately as there is nothing to do
        const toolState = cornerstoneTools.getToolState(evt.currentTarget, this.name);

        if (!toolState) {
            return;
        }

        const { image, element } = eventData;
        const config = this.configuration;
        const seriesModule = window.cornerstone.metaData.get(
            'generalSeriesModule',
            image.imageId
        );
        const modality = seriesModule ? seriesModule.modality : null;

        // We have tool data for this element - iterate over each one and draw it
        const context = getNewContext(eventData.canvasContext.canvas);
        const lineWidth = cornerstoneTools.toolStyle.getToolWidth();
        const { renderDashed } = config;
        const lineDash = cornerstoneTools.getModule('globalConfiguration').configuration.lineDash;

        let initFontSize = cornerstoneTools.textStyle.getFontSize();


        for (let i = 0; i < toolState.data.length; i++) {
            const data = toolState.data[i];

            if (data.visible === false) {
                continue;
            }

            draw(context, context => {
               // let color = cornerstoneTools.toolColors.getColorIfActive(data);
                let color = cornerstoneTools.toolColors.getColorIfActive(data);
                data.color = data.color || cornerstoneTools.toolColors.getToolColor(data);

                data.fontSize = data.fontSize !== undefined? data.fontSize : cornerstoneTools.textStyle.getFontSize();
                data.textVisible = data.textVisible !== undefined? data.textVisible : this.options.annotationTextVisible;

                // FIXME: ???
                // data.fontSize = 12;
                cornerstoneTools.textStyle.setFont(`${data.fontSize}px Roboto`);
                cornerstoneTools.textStyle.setFontSize(data.fontSize);

                if(data.invalidated && (data.who !== this.initialConfiguration.user || data.when !== new Date().toLocaleString())) {
                    data.who = this.initialConfiguration.user;
                    data.when = new Date().toLocaleString();
                    if(this.initialConfiguration.momentLib && this.initialConfiguration.defaultDateTimeFormat) {
                        data.whenStr = this.initialConfiguration.momentLib().format(this.initialConfiguration.defaultDateTimeFormat);
                    } else {
                        data.whenStr = new Date().toLocaleString();
                    }
                }

                let fillColor;

                if (data.active) {
                    if (data.handles.invalidHandlePlacement) {
                        color = config.invalidColor;
                        fillColor = config.invalidColor;
                    } else {
                        color = cornerstoneTools.toolColors.getColorIfActive(data);
                        fillColor = cornerstoneTools.toolColors.getFillColor();
                    }
                } else {
                    fillColor = cornerstoneTools.toolColors.getToolColor();
                }

                let options = { color };

                if (renderDashed) {
                    options.lineDash = lineDash;
                }

                if (data.handles.points.length) {
                    const points = data.handles.points;

                    drawJoinedLines(context, element, points[0], points, options);

                    if (data.polyBoundingBox) {
                        drawJoinedLines(
                            context,
                            element,
                            points[points.length - 1],
                            [points[0]],
                            options
                        );
                    } else {
                        drawJoinedLines(
                            context,
                            element,
                            points[points.length - 1],
                            [config.mouseLocation.handles.start],
                            options
                        );
                    }
                }

                // Draw handles

                options = {
                    color,
                    fill: fillColor,
                };

                if (config.alwaysShowHandles || (data.active && data.polyBoundingBox)) {
                    // Render all handles
                    options.handleRadius = config.activeHandleRadius;

                    if (this.configuration.drawHandles) {
                        drawHandles(context, eventData, data.handles.points, options);
                    }
                }

                if (data.canComplete) {
                    // Draw large handle at the origin if can complete drawing
                    options.handleRadius = config.completeHandleRadius;
                    const handle = data.handles.points[0];

                    if (this.configuration.drawHandles) {
                        drawHandles(context, eventData, [handle], options);
                    }
                }

                if (data.active && !data.polyBoundingBox) {
                    // Draw handle at origin and at mouse if actively drawing
                    options.handleRadius = config.activeHandleRadius;

                    if (this.configuration.drawHandles) {
                        drawHandles(
                            context,
                            eventData,
                            config.mouseLocation.handles,
                            options
                        );
                    }

                    const firstHandle = data.handles.points[0];

                    if (this.configuration.drawHandles) {
                        drawHandles(context, eventData, [firstHandle], options);
                    }
                }

                // Update textbox stats
                if (data.invalidated === true && !data.active) {
                    if (data.meanStdDev && data.meanStdDevSUV && data.area) {
                        super.throttledUpdateCachedStats(image, element, data);
                    } else {
                        super.updateCachedStats(image, element, data);
                    }
                }

                const columnPixelSpacing = image.columnPixelSpacingEx || 1;
                const rowPixelSpacing = image.rowPixelSpacingEx || 1;
                const scaling = columnPixelSpacing * rowPixelSpacing;
                data.area = data.area ? freehand.freehandArea(data.handles.points, scaling) : data.area;

                if (!data.canComplete && data.area) {
                    const rowPixelSpacing = image ? image.rowPixelSpacingEx : undefined;
                    const colPixelSpacing = image ? image.columnPixelSpacingEx : undefined;
                    if (colPixelSpacing !== undefined || rowPixelSpacing !== undefined) {
                        const calcLen = (point1, point2) => {
                            const dx =
                                (point2.x - point1.x) * (colPixelSpacing || 1);
                            const dy =
                                (point2.y - point1.y) * (rowPixelSpacing || 1);
                            return Math.sqrt(dx * dx + dy * dy);
                        }
                        // const length = Math.sqrt(dx * dx + dy * dy);
                        const points = data.handles.points;
                        let maxPoint1 = undefined;
                        let maxPoint2 = undefined;
                        let maxLen = 0;
                        for (const p1 of points) {
                            for (const p2 of points) {
                                const lenLocal = calcLen(p1, p2);
                                if (lenLocal > maxLen) {
                                    maxLen = lenLocal;
                                    maxPoint1 = p1;
                                    maxPoint2 = p2;
                                }
                            }
                        }

                        data.maxLen = maxLen;

                        if (maxPoint1 && maxPoint2) {
                            const milimetrWidth = 1.0 / colPixelSpacing;
                            const milimetrHeight = 1.0 / rowPixelSpacing;

                            const ellipseOptions = { color };

                            const midX = (maxPoint1.x + maxPoint2.x) / 2;
                            const midY = (maxPoint1.y + maxPoint2.y) / 2;
                            const fW = milimetrWidth * (maxLen + 1) / 2;
                            const fH = milimetrHeight * (maxLen + 1) / 2;

                            const lt = {
                                x: midX - fW,
                                y: midY - fH
                            };
                            const br = {
                                x: midX + fW,
                                y: midY + fH
                            };

                            drawEllipse(
                                context,
                                element,
                                lt,
                                br,
                                ellipseOptions,
                                'pixel'
                            );
                        }
                    }
                }


                const tooltipText =  `${data.who} (${data.whenStr})` ;

                if(data && data.polyBoundingBox && (data.active || this.options.showUser) && data.who) {
                    drawLinkedTextBox(
                        context,
                        element,
                        {
                            x: data.polyBoundingBox.left,
                            y: data.polyBoundingBox.top,
                        },
                        tooltipText,
                        data.handles,
                        textBoxAnchorPoints,
                        "white",
                        lineWidth,
                        0,
                        false
                    );
                }

                // Only render text if polygon ROI has been completed and freehand 'shiftKey' mode was not used:
                if (data.polyBoundingBox && !data.handles.textBox.freehand) {
                    // If the textbox has not been moved by the user, it should be displayed on the right-most
                    // Side of the tool.
                    if (!data.handles.textBox.hasMoved) {
                        // Find the rightmost side of the polyBoundingBox at its vertical center, and place the textbox here
                        // Note that this calculates it in image coordinates
                        data.handles.textBox.x =
                            data.polyBoundingBox.left + data.polyBoundingBox.width;
                        data.handles.textBox.y =
                            data.polyBoundingBox.top + data.polyBoundingBox.height / 2;
                    }

                    const text = textBoxText.call(this, data);

                    if(data.textVisible) {
                        drawLinkedTextBox(
                            context,
                            element,
                            data.handles.textBox,
                            text,
                            data.handles.points,
                            textBoxAnchorPoints,
                            //color,
                            "white",
                            lineWidth,
                            0,
                            true
                        );
                    }
                }
                cornerstoneTools.textStyle.setFontSize(initFontSize);
                cornerstoneTools.textStyle.setFont(`${initFontSize}px Roboto`);
            });
        }

        function textBoxText(data) {
            const { meanStdDev, meanStdDevSUV, area } = data;
            // Define an array to store the rows of text for the textbox
            const textLines = [];

            // If the mean and standard deviation values are present, display them
            if (meanStdDev && meanStdDev.mean !== undefined) {
                // If the modality is CT, add HU to denote Hounsfield Units
                let moSuffix = '';

                if (modality === 'CT') {
                    moSuffix = 'HU';
                }
                data.unit = moSuffix;

                // Create a line of text to display the mean and any units that were specified (i.e. HU)
                let meanText = `Mean: ${numbersWithCommas(
                    meanStdDev.mean.toFixed(2)
                )} ${moSuffix}`;
                // Create a line of text to display the standard deviation and any units that were specified (i.e. HU)
                let stdDevText = `StdDev: ${numbersWithCommas(
                    meanStdDev.stdDev.toFixed(2)
                )} ${moSuffix}`;

                // If this image has SUV values to display, concatenate them to the text line
                if (meanStdDevSUV && meanStdDevSUV.mean !== undefined) {
                    const SUVtext = ' SUV: ';

                    meanText +=
                        SUVtext + numbersWithCommas(meanStdDevSUV.mean.toFixed(2));
                    stdDevText +=
                        SUVtext + numbersWithCommas(meanStdDevSUV.stdDev.toFixed(2));
                }

                // Add these text lines to the array to be displayed in the textbox
                textLines.push(meanText);
                textLines.push(stdDevText);
            }

            // If the area is a sane value, display it
            if (area) {
                // Determine the area suffix based on the pixel spacing in the image.
                // If pixel spacing is present, use millimeters. Otherwise, use pixels.
                // This uses Char code 178 for a superscript 2
                let suffix = ` mm${String.fromCharCode(178)}`;

                // const { rowPixelSpacing, colPixelSpacing } = getPixelSpacing(image);
                const rowPixelSpacing = image ? image.rowPixelSpacingEx : undefined;
                const colPixelSpacing = image ? image.columnPixelSpacingEx : undefined;

                if (!rowPixelSpacing || !colPixelSpacing) {
                    suffix = ` pixels${String.fromCharCode(178)}`;
                }

                // Create a line of text to display the area and its units
                const areaText = `Area: ${numbersWithCommas(area.toFixed(2))}${suffix}`;

                // Add this text line to the array to be displayed in the textbox
                textLines.push(areaText);

                if (data.maxLen) {
                    const v = data.maxLen * 1000;
                    const spot = v + 1000;
                    const gldText = `GLD: ${v.toFixed(2)} μm`;
                    textLines.push(gldText);
                    const spotText = `Spot size: ${spot.toFixed(2)} μm`;
                    textLines.push(spotText);
                }
            }

            return textLines;
        }

        function textBoxAnchorPoints(handles) {
            return handles;
        }
    }

}

function defaultFreehandConfiguration() {
    return {
        mouseLocation: {
            handles: {
                start: {
                    highlight: true,
                    active: true,
                },
            },
        },
        spacing: 1,
        activeHandleRadius: 3,
        completeHandleRadius: 6,
        completeHandleRadiusTouch: 28,
        alwaysShowHandles: false,
        invalidColor: 'crimson',
        currentHandle: 0,
        currentTool: -1,
        drawHandles: true,
        renderDashed: false,
    };
}

